import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Mail, MessageCircle, Instagram, Linkedin, ArrowUpRight } from "lucide-react";
import { BRAND, Reveal, buildWhatsAppUrl } from "./shared";

const FAQS = [
  {
    q: "Do I need any equipment to start?",
    a: "Not to start. Programs are bodyweight-first. As you progress, we might add a resistance band or a pair of dumbbells — nothing more. If you have equipment, we'll use it thoughtfully.",
  },
  {
    q: "How much time do I need each week?",
    a: "Most clients train 3–4 times a week, around 40–50 minutes per session. We build the plan around your real schedule, not an idealised one.",
  },
  {
    q: "Is this suitable for complete beginners?",
    a: "Absolutely. A large part of the community started from zero. We focus on movement quality first, then build strength progressively.",
  },
  {
    q: "How is coaching delivered?",
    a: "Coaching is delivered through live online Zoom sessions. Depending on the program you choose, you'll attend either one-on-one coaching or group sessions, supported by a personalized training plan tailored to your goals. You'll also receive weekly check-ins via WhatsApp and direct access to Aditya for guidance, questions, and ongoing support.",
  },
  {
    q: "What if I have an existing injury?",
    a: "We discuss it in the free consultation. Programs are adapted around injuries or limitations, and we work in tandem with your physio when needed.",
  },
  {
    q: "How long is the initial commitment?",
    a: "Most people start with a 12-week block — long enough to see real change without feeling locked in. After that, we continue month-to-month.",
  },
];

export function FAQ() {
  return (
    <section id="faq" className="py-24 md:py-32">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <Reveal><p className="eyebrow">Frequently asked</p></Reveal>
          <Reveal delay={80}>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight mt-6 text-brand-ink">
              Answers to help you
              <br />
              <em className="italic text-brand-accent">get started.</em>
            </h2>
          </Reveal>
        </div>

        <Reveal delay={140}>
          <Accordion type="single" collapsible className="mt-12 md:mt-16 border-t border-brand-border">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b border-brand-border"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="text-left font-serif text-xl md:text-2xl text-brand-ink hover:no-underline py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-brand-ink2 text-base md:text-lg font-sans leading-relaxed pb-8">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}

export function FinalCTA({ onBookClick }) {
  return (
    <section id="contact" className="bg-brand-bg text-brand-ink py-24 md:py-32 grain relative overflow-hidden border-t border-brand-border">
      <div className="absolute inset-0 pointer-events-none opacity-30" style={{background: 'radial-gradient(600px circle at 20% 20%, rgba(240,120,44,0.18), transparent 60%)'}} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <Reveal>
          <p className="eyebrow">Ready when you are</p>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 max-w-3xl text-brand-ink">
            Ready to build
            <br />
            <em className="italic text-brand-accent">real strength?</em>
          </h2>
        </Reveal>
        <Reveal delay={160}>
          <p className="mt-8 text-lg md:text-xl text-brand-ink2 max-w-2xl font-sans leading-relaxed">
            Book a free 20-minute consultation. No hard sell. We'll talk about
            your goals, your schedule, and whether this is the right fit.
          </p>
        </Reveal>

        <Reveal delay={220}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button onClick={onBookClick} className="btn-accent" data-testid="final-book-btn">
              Book a Free Consultation
            </button>
            <a
              href={buildWhatsAppUrl("Hi Aditya, I'd like to know more about Har Ghar Strength.")}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline"
              data-testid="final-whatsapp-link"
            >
              <MessageCircle className="h-4 w-4" />
              Message on WhatsApp
            </a>
          </div>
        </Reveal>

        <Reveal delay={280}>
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 pt-10 border-t border-brand-border">
            <a
              href={buildWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start justify-between gap-3"
              data-testid="contact-whatsapp"
            >
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-widest text-brand-ink2">WhatsApp</p>
                <p className="mt-2 font-serif text-base md:text-lg text-brand-ink group-hover:text-brand-accent transition-colors whitespace-nowrap">{BRAND.whatsappDisplay}</p>
              </div>
              <ArrowUpRight className="h-5 w-5 mt-2 text-brand-ink2 group-hover:text-brand-accent transition-colors shrink-0" />
            </a>
            <a
              href={`mailto:${BRAND.email}`}
              className="group flex items-start justify-between gap-3"
              data-testid="contact-email"
            >
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-widest text-brand-ink2">Email</p>
                <p className="mt-2 font-serif text-base md:text-lg text-brand-ink group-hover:text-brand-accent transition-colors whitespace-nowrap overflow-hidden text-ellipsis">{BRAND.email}</p>
              </div>
              <ArrowUpRight className="h-5 w-5 mt-2 text-brand-ink2 group-hover:text-brand-accent transition-colors shrink-0" />
            </a>
            <a
              href={`https://instagram.com/${BRAND.instagram}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start justify-between gap-3"
              data-testid="contact-instagram"
            >
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-widest text-brand-ink2">Instagram</p>
                <p className="mt-2 font-serif text-base md:text-lg text-brand-ink group-hover:text-brand-accent transition-colors whitespace-nowrap">@{BRAND.instagram}</p>
              </div>
              <ArrowUpRight className="h-5 w-5 mt-2 text-brand-ink2 group-hover:text-brand-accent transition-colors shrink-0" />
            </a>
            <a
              href={BRAND.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start justify-between gap-3"
              data-testid="contact-linkedin"
            >
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-widest text-brand-ink2">LinkedIn</p>
                <p className="mt-2 font-serif text-base md:text-lg text-brand-ink group-hover:text-brand-accent transition-colors whitespace-nowrap">Aditya Kulhari</p>
              </div>
              <ArrowUpRight className="h-5 w-5 mt-2 text-brand-ink2 group-hover:text-brand-accent transition-colors shrink-0" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="bg-brand-bg text-brand-ink2 border-t border-brand-border py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm font-sans">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 bg-white rounded-full overflow-hidden flex items-center justify-center border border-brand-border">
            <img
              src={BRAND.logo}
              alt={BRAND.name}
              className="w-full h-full object-cover"
              style={{ objectPosition: "center 32%", transform: "scale(1.4)" }}
            />
          </div>
          <span className="font-serif text-brand-ink text-base">Har Ghar Strength</span>
        </div>
        <p>© {new Date().getFullYear()} Har Ghar Strength. Strength, from anywhere.</p>
        <div className="flex items-center gap-4">
          <a
            href={BRAND.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-brand-accent transition-colors"
            data-testid="footer-linkedin"
            aria-label="LinkedIn"
          >
            <Linkedin className="h-4 w-4" />
          </a>
          <a
            href={`https://instagram.com/${BRAND.instagram}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-brand-accent transition-colors"
            data-testid="footer-instagram"
            aria-label="Instagram"
          >
            <Instagram className="h-4 w-4" />
          </a>
          <a
            href={`mailto:${BRAND.email}`}
            className="hover:text-brand-accent transition-colors"
            data-testid="footer-email"
            aria-label="Email"
          >
            <Mail className="h-4 w-4" />
          </a>
        </div>
      </div>
    </footer>
  );
}

export function WhatsAppFloat() {
  return (
    <a
      href={buildWhatsAppUrl("Hi Aditya, I'd like to book a free consultation.")}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-5 right-5 md:bottom-8 md:right-8 z-40 bg-[#25D366] text-white h-14 w-14 md:h-16 md:w-16 flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
      aria-label="Chat on WhatsApp"
      data-testid="whatsapp-float"
    >
      <MessageCircle className="h-6 w-6 md:h-7 md:w-7" />
    </a>
  );
}
